import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { AnimatePresence, motion } from "framer-motion";
import { PropsWithChildren, useCallback, useEffect, useState } from "react";
import { createEnumParam, useQueryParams, withDefault } from "use-query-params";

import AccountInfo from "@components/account/AccountInfo";
import AccountMenu from "@components/account/AccountMenu";
import AccountPackage from "@components/account/AccountPackage";
import AccountPrivacy from "@components/account/AccountPrivacy";
import AccountReportProblem from "@components/account/AccountReportProblem";
import PackagePurchaseOptionPurchase from "@components/account/PackagePurchaseOptionPurchase";
import SecurityAccountDeletion from "@components/account/SecurityAccountDeletion";
import SecurityMarketing from "@components/account/SecurityMarketing";
import TwoFactorAuthenticationSetup from "@components/account/TwoFactorAuthenticationSetup";
import ClientPasswordUpdateForm from "@components/forms/ClientPasswordUpdateForm";
import EmailSecurityForm from "@components/forms/EmailSecurityForm";
import MobileHeader from "@components/MobileHeader";
import { AccountSectionEnum } from "@typing/Enums";
import { GetClientWithAccountQuery, GetPackagePurchaseOptionsQuery } from "@typing/Generated";
import { fadeAnimation, slideHorizontalAnimation } from "@utils/animationUtils";
import { isMobile } from "@utils/platformUtils";

type PackagePurchaseOption = GetPackagePurchaseOptionsQuery["packagePurchaseOptions"][number];

type Direction = "right" | "left";

type Props = {
  client: GetClientWithAccountQuery["client"];
  journeyId: string;
};

type SectionProps = PropsWithChildren<{
  handleBack: (section: AccountSectionEnum) => void;
  section: AccountSectionEnum;
}>;

const Section = ({ children, handleBack, section }: SectionProps) => (
  <div>
    <MobileHeader handleBack={() => handleBack(section)} titleKey={`pages.client.account.menu.${section}`} />
    <div className="ion-padding-start ion-padding-end">{children}</div>
  </div>
);

const Account = ({ client, journeyId }: Props) => {
  const accountSectionParam = createEnumParam<AccountSectionEnum>(Object.values(AccountSectionEnum));
  const [swipeDirection, setSwipeDirection] = useState<Direction>("right");

  const [query, setQuery] = useQueryParams({
    section: withDefault(accountSectionParam, isMobile() ? AccountSectionEnum.MENU : AccountSectionEnum.INFO)
  });

  const [currentSection, setCurrentSection] = useState<AccountSectionEnum>(query.section);

  useEffect(() => {
    if (query.section !== currentSection) setQuery({ section: currentSection });
  }, [setQuery, query.section, currentSection]);

  const [currentPackagePurchaseOption, setCurrentPackagePurchaseOption] = useState<PackagePurchaseOption | null>(null);

  const handleCurrentSectionChange = (newSection: AccountSectionEnum, direction: Direction) => {
    setSwipeDirection(direction);
    setTimeout(() => setCurrentSection(newSection), 100);
  };

  const clearCurrentPackagePurchaseOption = useCallback(() => {
    setCurrentPackagePurchaseOption(null);

    if (isMobile()) {
      handleCurrentSectionChange(AccountSectionEnum.PACKAGE, "left");
    }
  }, []);

  const generateHandlePackagePurchaseOptionSelect = (newPackagePurchaseOption: PackagePurchaseOption | null) => {
    setCurrentPackagePurchaseOption(newPackagePurchaseOption);

    if (isMobile()) {
      handleCurrentSectionChange(AccountSectionEnum.PURCHASE_HOURS, "right");
    }
  };

  const privacySections = [
    AccountSectionEnum.PRIVACY_ACCOUNT_DELETION,
    AccountSectionEnum.PRIVACY_CHANGE_PASSWORD,
    AccountSectionEnum.PRIVACY_EMAIL_NOTIFICATIONS,
    AccountSectionEnum.PRIVACY_MARKETING,
    AccountSectionEnum.PRIVACY_TWO_FACTOR
  ];

  const packageSections = [AccountSectionEnum.PURCHASE_HOURS];

  const handleBack = (section: AccountSectionEnum) => {
    let newSection = AccountSectionEnum.MENU;
    if (privacySections.includes(section)) {
      newSection = AccountSectionEnum.PRIVACY;
    } else if (packageSections.includes(section)) {
      newSection = AccountSectionEnum.PACKAGE;
    }

    if (section === AccountSectionEnum.PURCHASE_HOURS && currentPackagePurchaseOption) {
      clearCurrentPackagePurchaseOption();
    }

    handleCurrentSectionChange(newSection, "left");
  };

  const animationVariants = isMobile() ? slideHorizontalAnimation(swipeDirection) : fadeAnimation;

  return (
    <IonGrid className="ion-no-padding">
      <IonRow>
        <IonCol className="ion-hide-lg-down" size="auto">
          <AccountMenu
            currentSection={currentSection}
            handleCurrentSectionChange={handleCurrentSectionChange}
            journeyId={journeyId}
          />
        </IonCol>
        <IonCol className={currentSection ? "" : "ion-hide-lg-down"}>
          <AnimatePresence>
            {(() => {
              switch (currentSection) {
                case AccountSectionEnum.INFO:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={AccountSectionEnum.INFO}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <Section handleBack={handleBack} section={AccountSectionEnum.INFO}>
                        <AccountInfo client={client} />
                      </Section>
                    </motion.div>
                  );
                // In order to have a smooth mobile animation, we need to render the mobile menu in the same animate presence
                case AccountSectionEnum.MENU:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial={false}
                      key={AccountSectionEnum.MENU}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <AccountMenu
                        currentSection={currentSection}
                        handleCurrentSectionChange={handleCurrentSectionChange}
                        journeyId={journeyId}
                      />
                    </motion.div>
                  );
                case AccountSectionEnum.PACKAGE:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={AccountSectionEnum.PACKAGE}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <Section handleBack={handleBack} section={AccountSectionEnum.PACKAGE}>
                        <AccountPackage
                          clearCurrentPackagePurchaseOption={clearCurrentPackagePurchaseOption}
                          client={client}
                          currentPackagePurchaseOption={currentPackagePurchaseOption}
                          generateHandlePackagePurchaseOptionSelect={generateHandlePackagePurchaseOptionSelect}
                        />
                      </Section>
                    </motion.div>
                  );
                case AccountSectionEnum.PRIVACY:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={AccountSectionEnum.PRIVACY}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <Section handleBack={handleBack} section={AccountSectionEnum.PRIVACY}>
                        <AccountPrivacy client={client} handleCurrentSectionChange={handleCurrentSectionChange} />
                      </Section>
                    </motion.div>
                  );
                case AccountSectionEnum.PRIVACY_ACCOUNT_DELETION:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={AccountSectionEnum.PRIVACY_ACCOUNT_DELETION}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <Section handleBack={handleBack} section={AccountSectionEnum.PRIVACY_ACCOUNT_DELETION}>
                        <SecurityAccountDeletion />
                      </Section>
                    </motion.div>
                  );
                case AccountSectionEnum.PRIVACY_CHANGE_PASSWORD:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={AccountSectionEnum.PRIVACY_CHANGE_PASSWORD}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <Section handleBack={handleBack} section={AccountSectionEnum.PRIVACY_CHANGE_PASSWORD}>
                        <ClientPasswordUpdateForm autofocus={false} />
                      </Section>
                    </motion.div>
                  );
                case AccountSectionEnum.PRIVACY_EMAIL_NOTIFICATIONS:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={AccountSectionEnum.PRIVACY_EMAIL_NOTIFICATIONS}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <Section handleBack={handleBack} section={AccountSectionEnum.PRIVACY_EMAIL_NOTIFICATIONS}>
                        <EmailSecurityForm client={client} />
                      </Section>
                    </motion.div>
                  );
                case AccountSectionEnum.PRIVACY_MARKETING:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={AccountSectionEnum.PRIVACY_MARKETING}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <Section handleBack={handleBack} section={AccountSectionEnum.PRIVACY_MARKETING}>
                        <SecurityMarketing client={client} />
                      </Section>
                    </motion.div>
                  );
                case AccountSectionEnum.PRIVACY_TWO_FACTOR:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={AccountSectionEnum.PRIVACY_TWO_FACTOR}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <Section handleBack={handleBack} section={AccountSectionEnum.PRIVACY_TWO_FACTOR}>
                        <TwoFactorAuthenticationSetup client={client} />
                      </Section>
                    </motion.div>
                  );
                case AccountSectionEnum.PURCHASE_HOURS:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={AccountSectionEnum.PURCHASE_HOURS}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <Section handleBack={handleBack} section={AccountSectionEnum.PURCHASE_HOURS}>
                        {currentPackagePurchaseOption && (
                          <PackagePurchaseOptionPurchase
                            clearCurrentPackagePurchaseOption={clearCurrentPackagePurchaseOption}
                            clientId={client.id}
                            packagePurchaseOption={currentPackagePurchaseOption}
                          />
                        )}
                      </Section>
                    </motion.div>
                  );
                case AccountSectionEnum.REPORT_A_PROBLEM:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={AccountSectionEnum.REPORT_A_PROBLEM}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <Section handleBack={handleBack} section={AccountSectionEnum.REPORT_A_PROBLEM}>
                        <AccountReportProblem client={client} journeyId={journeyId} />
                      </Section>
                    </motion.div>
                  );
              }
            })()}
          </AnimatePresence>
        </IonCol>
      </IonRow>
    </IonGrid>
  );
};

export default Account;
